import {
	type ColumnDef,
	type FilterFn,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { Badge } from "@lawline/ui/components/badge";
import { Input } from "@lawline/ui/components/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";

import { cn } from "@lawline/ui/core/cn";

import type { RosterRow } from "../tenant.types";
import { RowActions } from "./member-actions";

const STATUS_CLASS: Record<RosterRow["status"], string> = {
	active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
	suspended: "bg-red-500/15 text-red-700 dark:text-red-400",
	pending: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
};

const filterRoster: FilterFn<RosterRow> = (row, _columnId, value) => {
	const q = String(value).toLowerCase();
	const r = row.original;
	return (
		r.name.toLowerCase().includes(q) ||
		r.email.toLowerCase().includes(q) ||
		r.role.toLowerCase().includes(q) ||
		r.status.includes(q)
	);
};

export function MembersTable({ rows: data, action }: { rows: RosterRow[]; action?: ReactNode }) {
	const t = useTranslations("tenant.members");
	const [globalFilter, setGlobalFilter] = useState("");
	const columns = useMemo<ColumnDef<RosterRow>[]>(
		() => [
			{
				accessorKey: "email",
				header: t("columns.email"),
				cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
			},
			{
				accessorKey: "role",
				header: t("columns.role"),
				cell: ({ row }) => (
					<Badge variant="secondary" className="capitalize">
						{row.original.role}
					</Badge>
				),
			},
			{
				accessorKey: "status",
				header: t("columns.status"),
				cell: ({ row }) => (
					<Badge className={cn("border-transparent", STATUS_CLASS[row.original.status])}>
						{t(`status.${row.original.status}`)}
					</Badge>
				),
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<div className="text-right">
						<RowActions row={row.original} />
					</div>
				),
			},
		],
		[t],
	);
	const table = useReactTable({
		data,
		columns,
		state: { globalFilter },
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: filterRoster,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	const rows = table.getRowModel().rows;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center justify-between gap-2">
				<div className="relative w-full max-w-xs">
					<Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
					<Input
						value={globalFilter}
						onChange={(e) => setGlobalFilter(e.target.value)}
						placeholder={t("searchPlaceholder")}
						className="pl-8"
					/>
				</div>
				{action}
			</div>

			<div className="overflow-hidden rounded-md border">
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((group) => (
							<TableRow key={group.id}>
								{group.headers.map((header) => (
									<TableHead key={header.id}>
										{flexRender(header.column.columnDef.header, header.getContext())}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{rows.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="text-muted-foreground h-20 text-center text-sm"
								>
									{t("noResults")}
								</TableCell>
							</TableRow>
						) : (
							rows.map((row) => (
								<TableRow key={row.id}>
									{row.getVisibleCells().map((cell) => (
										<TableCell key={cell.id}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
